import { useTranslation } from '@jq4race/i18n';
import { Link } from '@tanstack/react-router';
import { Clock, ListChecks, ScanLine, Search, Zap } from 'lucide-react';

import type { Identity } from '../../auth/domain/resolve-identity';
import { usePendingSyncCount } from '../../offline/ui/use-pending-sync-count';

const NAV_ITEMS = [
  {
    to: '/queue' as const,
    icon: ListChecks,
    labelKey: 'nav.queue',
    ariaLabelKey: 'nav.queueAria',
  },
  {
    to: '/checkin' as const,
    icon: ScanLine,
    labelKey: 'nav.checkin',
    ariaLabelKey: 'nav.checkinAria',
  },
  {
    to: '/flash' as const,
    icon: Zap,
    labelKey: 'nav.flash',
    ariaLabelKey: 'nav.flashAria',
  },
  {
    to: '/search' as const,
    icon: Search,
    labelKey: 'nav.search',
    ariaLabelKey: 'nav.searchAria',
  },
  // pwa-offline-first Slice 6 (task 6.8) — reaches PendingItemsPage. Same
  // shape as every other item above (Extract-Before-Mock: nothing new to
  // test here beyond what the 4 pre-existing, already-untested entries in
  // this file already exercise structurally).
  {
    to: '/pending' as const,
    icon: Clock,
    labelKey: 'nav.pending',
    ariaLabelKey: 'nav.pendingAria',
  },
] as const;

interface BottomNavProps {
  /** pwa-offline-first Slice 6 (coordinator finding #4) — see ConnectivityBadge's own doc comment. */
  identity: Identity | null;
}

export function BottomNav({ identity }: BottomNavProps) {
  const { t } = useTranslation('shell');
  const pendingCount = usePendingSyncCount(identity);

  return (
    <nav
      aria-label={t('nav.mainAria')}
      // Navy "island": the bottom nav keeps the dark theme while the rest of the
      // PWA runs cream. Scoped via the shared token attribute so no colors are
      // hardcoded — border/background/text tokens resolve to their navy values.
      data-jq-theme="navy"
      className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background/95 backdrop-blur-md"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="mx-auto grid max-w-md grid-cols-5">
        {NAV_ITEMS.map(({ to, icon: Icon, labelKey, ariaLabelKey }) => (
          <Link
            key={to}
            to={to}
            aria-label={t(ariaLabelKey)}
            className="relative flex min-h-[68px] flex-col items-center justify-center gap-1.5 transition-colors"
            activeProps={{ className: 'text-jq-gold-bright', 'aria-current': 'page' as const }}
            inactiveProps={{ className: 'text-muted-foreground' }}
          >
            {({ isActive }) => (
              <>
                {isActive ? (
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 top-0 h-0.5 bg-jq-gold-bright"
                  />
                ) : null}
                <span className="relative">
                  <Icon className="size-6" strokeWidth={1.5} />
                  {to === '/pending' && pendingCount > 0 && (
                    <span
                      aria-hidden="true"
                      className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground"
                    >
                      {pendingCount > 9 ? '9+' : pendingCount}
                    </span>
                  )}
                </span>
                <span className="text-[12px] font-medium uppercase tracking-wide leading-none">
                  {t(labelKey)}
                </span>
              </>
            )}
          </Link>
        ))}
      </div>
    </nav>
  );
}
